import React, { useState, useEffect, useCallback } from 'react';
import { useOrgProject } from '../contexts/OrgProjectContext';
import { apiFetch } from '../apiClient';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function PageTypeTag({ type }) {
  const cfg = {
    entity:         { bg: '#DDF4FF', color: '#0969DA', label: 'Entity' },
    concept:        { bg: '#FFF8C5', color: '#9A6700', label: 'Concept' },
    assessment:     { bg: '#DAFBE1', color: '#1A7F37', label: 'Assessment' },
    recommendation: { bg: '#FFEBE9', color: '#CF222E', label: 'Recommendation' },
  };
  const c = cfg[type] || { bg: '#EFF2F5', color: '#57606A', label: type };
  return (
    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
      background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

function MarkdownView({ content }) {
  if (!content) return <span style={{ color: '#8B949E', fontStyle: 'italic' }}>No content</span>;
  // Simple markdown renderer — headings, bold, lists
  const lines = content.split('\n');
  return (
    <div style={{ fontSize: 12, lineHeight: 1.7, color: '#1A1F2E' }}>
      {lines.map((line, i) => {
        if (line.startsWith('## ')) return <h3 key={i} style={{ margin: '10px 0 4px', fontSize: 13, color: '#0969DA' }}>{line.slice(3)}</h3>;
        if (line.startsWith('# '))  return <h2 key={i} style={{ margin: '12px 0 6px', fontSize: 15, color: '#1A1F2E' }}>{line.slice(2)}</h2>;
        if (line.startsWith('- '))  return <div key={i} style={{ paddingLeft: 12, color: '#57606A' }}>• {line.slice(2)}</div>;
        if (line.startsWith('**') && line.endsWith('**')) return <strong key={i}>{line.slice(2, -2)}</strong>;
        if (line === '---') return <hr key={i} style={{ border: 'none', borderTop: '1px solid #D0D7DE', margin: '8px 0' }} />;
        if (!line.trim()) return <div key={i} style={{ height: 4 }} />;
        return <div key={i}>{line}</div>;
      })}
    </div>
  );
}

function PageCard({ page, onSelect, selected }) {
  return (
    <div onClick={() => onSelect(page)} style={{
      padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
      border: `1px solid ${selected ? '#0969DA' : '#D0D7DE'}`,
      background: selected ? '#DDF4FF' : '#fff',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <PageTypeTag type={page.page_type} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#1A1F2E', flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {page.title}
        </span>
      </div>
      <div style={{ fontSize: 10, color: '#8B949E' }}>
        {page.app_id} · v{page.version} · {new Date(page.updated_at).toLocaleTimeString()}
      </div>
    </div>
  );
}

function MetricsRow({ metrics }) {
  if (!metrics?.length) return null;
  const byAgent = {};
  metrics.forEach(m => {
    if (!byAgent[m.agent_name]) byAgent[m.agent_name] = { count: 0, total_ms: 0, failed: 0 };
    byAgent[m.agent_name].count++;
    byAgent[m.agent_name].total_ms += m.duration_ms;
    if (m.status !== 'completed') byAgent[m.agent_name].failed++;
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {Object.entries(byAgent).map(([agent, stats]) => (
        <div key={agent} style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 8px', background: '#F6F8FA', borderRadius: 6, fontSize: 11 }}>
          <span style={{ fontWeight: 700, color: '#1A1F2E', minWidth: 140 }}>{agent}</span>
          <span style={{ color: '#57606A' }}>{stats.count} calls</span>
          <span style={{ color: '#0969DA', marginLeft: 'auto' }}>
            avg {Math.round(stats.total_ms / stats.count / 1000)}s
          </span>
          {stats.failed > 0 && (
            <span style={{ color: '#CF222E' }}>{stats.failed} failed</span>
          )}
        </div>
      ))}
    </div>
  );
}

function FileResultForm({ apps, onFiled }) {
  const [appId, setAppId]       = useState(apps[0] || '');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer]     = useState('');
  const [filing, setFiling]     = useState(false);
  const [filed, setFiled]       = useState(false);

  const handleFile = async () => {
    if (!appId || !question.trim() || !answer.trim()) return;
    setFiling(true);
    try {
      await apiFetch(`/api/wiki/file-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app_id: appId, question, answer }),
      });
      setFiled(true);
      setQuestion(''); setAnswer('');
      setTimeout(() => setFiled(false), 2000);
      onFiled && onFiled();
    } catch (e) {}
    setFiling(false);
  };

  return (
    <div style={{ marginTop: 16, padding: 12, background: '#F6F8FA',
      borderRadius: 8, border: '1px solid #D0D7DE' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#1A1F2E', marginBottom: 8 }}>
        📥 File Query Result into Wiki
      </div>
      <div style={{ fontSize: 10, color: '#57606A', marginBottom: 8 }}>
        Good answers shouldn't disappear into chat history — file them as wiki pages.
      </div>
      <select value={appId} onChange={e => setAppId(e.target.value)} style={{
        width: '100%', padding: '4px 6px', borderRadius: 6, fontSize: 11,
        border: '1px solid #D0D7DE', marginBottom: 6, background: '#fff',
      }}>
        {apps.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <input value={question} onChange={e => setQuestion(e.target.value)}
        placeholder="Question / topic..." style={{
          width: '100%', padding: '5px 8px', borderRadius: 6, fontSize: 11,
          border: '1px solid #D0D7DE', marginBottom: 6, outline: 'none',
        }} />
      <textarea value={answer} onChange={e => setAnswer(e.target.value)}
        placeholder="Answer / analysis to file..." rows={4} style={{
          width: '100%', padding: '5px 8px', borderRadius: 6, fontSize: 11,
          border: '1px solid #D0D7DE', marginBottom: 6, outline: 'none', resize: 'vertical',
        }} />
      <button onClick={handleFile} disabled={filing || !question.trim() || !answer.trim()} style={{
        padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700,
        background: filed ? '#1A7F37' : '#0969DA', color: '#fff', border: 'none',
        cursor: 'pointer', opacity: filing ? 0.6 : 1,
      }}>
        {filed ? '✓ Filed' : filing ? 'Filing...' : '📥 File into Wiki'}
      </button>
    </div>
  );
}

export default function WikiPanel({ wikiUpdateCount }) {
  const { orgId, projectId, contextVersion } = useOrgProject();
  const [pages, setPages]         = useState([]);
  const [selected, setSelected]   = useState(null);
  const [pageDetail, setPageDetail] = useState(null);
  const [searchQ, setSearchQ]     = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [metrics, setMetrics]     = useState([]);
  const [activeTab, setActiveTab] = useState('pages');
  const [appFilter, setAppFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading]     = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchPages = useCallback(async () => {
    setFetchError('');
    try {
      const params = new URLSearchParams();
      if (orgId)      params.set('org_id', orgId);
      if (projectId)  params.set('project_id', projectId);
      if (appFilter)  params.set('app_id', appFilter);
      if (typeFilter) params.set('page_type', typeFilter);
      const r = await apiFetch(`/api/wiki/pages?${params}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setPages(d.pages || []);
      setLastUpdated(new Date());
    } catch (e) {
      setFetchError(`Failed to load wiki pages: ${e.message}`);
      console.error('wiki pages fetch failed', e);
    }
  }, [appFilter, typeFilter, orgId, projectId]);

  const fetchMetrics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orgId)     params.set('org_id', orgId);
      if (projectId) params.set('project_id', projectId);
      const r = await apiFetch(`/api/wiki/metrics?${params}`);
      if (!r.ok) return;
      const d = await r.json();
      setMetrics(d.metrics || []);
    } catch (e) {}
  }, [orgId, projectId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchPages(), fetchMetrics()]);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchPages();
    fetchMetrics();
  }, [fetchPages, fetchMetrics, wikiUpdateCount, contextVersion]);

  // Auto-refresh every 10s so wiki stays live without needing a pipeline run
  useEffect(() => {
    const t = setInterval(() => { fetchPages(); fetchMetrics(); }, 10000);
    return () => clearInterval(t);
  }, [fetchPages, fetchMetrics]);

  const handleSelect = async (page) => {
    setSelected(page);
    setLoading(true);
    try {
      const r = await apiFetch(`/api/wiki/pages/${encodeURIComponent(page.id)}`);
      const d = await r.json();
      setPageDetail(d);
    } catch (e) { setPageDetail(page); }
    setLoading(false);
  };

  const handleSearch = async () => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    try {
      const params = new URLSearchParams();
      params.set('q', searchQ);
      if (orgId)     params.set('org_id', orgId);
      if (projectId) params.set('project_id', projectId);
      const r = await apiFetch(`/api/wiki/search?${params}`);
      const d = await r.json();
      setSearchResults(d.results || []);
    } catch (e) {}
  };

  const apps = [...new Set(pages.map(p => p.app_id))];
  const types = [...new Set(pages.map(p => p.page_type))];
  const displayPages = searchResults || pages;

  const tabStyle = (t) => ({
    padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
    cursor: 'pointer', border: 'none',
    background: activeTab === t ? '#0969DA' : 'transparent',
    color: activeTab === t ? '#fff' : '#57606A',
  });

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <span className="card-title-accent">📚</span> Wiki Knowledge Graph
        <span style={{ marginLeft: 8, fontSize: 10, background: '#DDF4FF', color: '#0969DA',
          padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>
          {pages.length} pages
        </span>
        {lastUpdated && (
          <span style={{ marginLeft: 6, fontSize: 9, color: '#8B949E' }}>
            ● live · {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <button onClick={handleRefresh} disabled={refreshing} style={{
          marginLeft: 8, padding: '2px 8px', borderRadius: 6, fontSize: 10,
          border: '1px solid #D0D7DE', background: '#fff', cursor: 'pointer',
          color: '#57606A',
        }}>
          {refreshing ? '⟳' : '↺'} Refresh
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {['pages', 'metrics'].map(t => (
            <button key={t} style={tabStyle(t)} onClick={() => setActiveTab(t)}>
              {t === 'pages' ? '📄 Pages' : '📊 Metrics'}
            </button>
          ))}
        </div>
      </div>

      {fetchError && (
        <div style={{ margin: '8px 16px', padding: '6px 10px', background: '#FFEBE9',
          border: '1px solid #FF818266', borderRadius: 6, fontSize: 11, color: '#CF222E' }}>
          ⚠ {fetchError}
        </div>
      )}

      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10, height: 'calc(100% - 48px)', overflow: 'hidden' }}>

        {activeTab === 'pages' && (
          <div style={{ display: 'flex', gap: 12, height: '100%', overflow: 'hidden' }}>
            {/* Left: page list */}
            <div style={{ width: 260, display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
              {/* Search */}
              <div style={{ display: 'flex', gap: 4 }}>
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="Search wiki..." style={{
                    flex: 1, padding: '5px 8px', borderRadius: 6, fontSize: 11,
                    border: '1px solid #D0D7DE', outline: 'none',
                  }} />
                <button onClick={handleSearch} style={{
                  padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                  background: '#0969DA', color: '#fff', border: 'none', fontWeight: 700,
                }}>🔍</button>
                {searchResults && (
                  <button onClick={() => { setSearchResults(null); setSearchQ(''); }} style={{
                    padding: '5px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                    background: '#EFF2F5', color: '#57606A', border: '1px solid #D0D7DE',
                  }}>✕</button>
                )}
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: 4 }}>
                <select value={appFilter} onChange={e => setAppFilter(e.target.value)} style={{
                  flex: 1, padding: '4px 6px', borderRadius: 6, fontSize: 10,
                  border: '1px solid #D0D7DE', background: '#fff',
                }}>
                  <option value="">All apps</option>
                  {apps.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{
                  flex: 1, padding: '4px 6px', borderRadius: 6, fontSize: 10,
                  border: '1px solid #D0D7DE', background: '#fff',
                }}>
                  <option value="">All types</option>
                  {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Page list */}
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {searchResults && (
                  <div style={{ fontSize: 10, color: '#57606A', padding: '2px 4px' }}>
                    {searchResults.length} results for "{searchQ}"
                  </div>
                )}
                {displayPages.length === 0 && (
                  <div style={{ color: '#8B949E', fontSize: 11, fontStyle: 'italic', padding: 8 }}>
                    No pages yet — run the pipeline to populate the Wiki
                  </div>
                )}
                {displayPages.map(p => (
                  <PageCard key={p.id || p.page_id} page={p}
                    onSelect={handleSelect}
                    selected={selected?.id === (p.id || p.page_id)} />
                ))}
              </div>
            </div>

            {/* Right: page detail */}
            <div style={{ flex: 1, overflowY: 'auto', background: '#F6F8FA',
              borderRadius: 8, border: '1px solid #D0D7DE', padding: 14 }}>
              {!selected && (
                <div style={{ color: '#8B949E', fontStyle: 'italic', fontSize: 12, textAlign: 'center', marginTop: 40 }}>
                  Select a page to view its content
                </div>
              )}
              {selected && loading && (
                <div style={{ color: '#0969DA', fontSize: 12 }}>Loading...</div>
              )}
              {selected && !loading && pageDetail && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <PageTypeTag type={pageDetail.page_type} />
                    <span style={{ fontWeight: 800, fontSize: 14, color: '#1A1F2E' }}>{pageDetail.title}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#8B949E' }}>
                      v{pageDetail.version} · {pageDetail.app_id}
                    </span>
                  </div>
                  {pageDetail.tags?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {pageDetail.tags.map(t => (
                        <span key={t} style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4,
                          background: '#EFF2F5', color: '#57606A' }}>{t}</span>
                      ))}
                    </div>
                  )}
                  <MarkdownView content={pageDetail.content} />
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div style={{ overflowY: 'auto' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#57606A', marginBottom: 8 }}>
              Agent Performance Metrics ({metrics.length} recorded)
            </div>
            <MetricsRow metrics={metrics} />
            {metrics.length === 0 && (
              <div style={{ color: '#8B949E', fontStyle: 'italic', fontSize: 12 }}>
                No metrics yet — run the pipeline to collect data
              </div>
            )}

            {/* File query result back into wiki */}
            <FileResultForm apps={[...new Set(pages.map(p => p.app_id))]} onFiled={fetchPages} />
          </div>
        )}
      </div>
    </div>
  );
}
